// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/PageHeader";
import PageTitle from "../../../components/PageTitle";
import PageBody from "../../../components/PageBody";
import { navigate, RouteComponentProps } from "@reach/router";
import {
  AuthentifiedUser,
  BroadcastListInfoResponseDTO,
  BroadcastListsSearchRequestDTO
} from "../../../../api/backend-openapi";
import { useStore } from "effector-react";
import { authentifiedUserStore } from "../../../../state/auth/AuthStore";
import { NboAction } from "../../../types/AuthTypes";
import { SpinIcon } from "../../../components/SpinIcon";
import { columns } from "./BroadcastListsColumns";
import { Button, Divider, message, Table } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import { displayError } from "../../../../utils/displayError";
import {
  requestDeleteBroadcastList,
  requestDuplicateBroadcastList,
  requestSearchBroadcastLists
} from "../../../../state/broadcastLists/BroadcastListsEffects";
import { orderDescend } from "../../../../utils/Constant";
import { SorterResult, TablePaginationConfig } from "antd/lib/table/interface";
import {
  preferedRowsPerPage,
  preferedRowsPerPageChanged
} from "../../../../state/localPreferences/localPreferences";
import BroadcastListsListSearchForm from "./BroadcastListsListSearchForm";
import {
  deleteBroadcastList,
  setBroadcastLists
} from "../../../../state/broadcastLists/BroadcastListsEvents";
import { BroadcastListsStore } from "../../../../state/broadcastLists/BroadcastListsStore";

const BroadcastListsScreen: React.FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();
  const [editMode] = useState<boolean>(false);
  const authentifiedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );
  const broadcastListStore: BroadcastListInfoResponseDTO[] | null = useStore(
    BroadcastListsStore
  );
  const canDelete: boolean =
    (authentifiedUser &&
      authentifiedUser.rights &&
      authentifiedUser.rights.nboBroadcastList &&
      typeof authentifiedUser.rights.nboBroadcastList.find(
        survey => survey === NboAction.nboDelete
      ) === "string") ||
    false;
  const canCreate: boolean =
    (authentifiedUser &&
      authentifiedUser.rights &&
      authentifiedUser.rights.nboBroadcastList &&
      typeof authentifiedUser.rights.nboBroadcastList.find(
        survey => survey === NboAction.nboCreate
      ) === "string") ||
    false;

  const rowsPerPage = useStore(preferedRowsPerPage);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: rowsPerPage,
    showSizeChanger: true
  });

  const [dataHasLoaded, setDataHasLoaded] = useState<boolean>(true);
  const [searchDto, setSearchDto] = useState<
    BroadcastListsSearchRequestDTO | {}
  >({});

  const handleTableChange = async (
    newPagination: any,
    _filters: any,
    sorter: SorterResult<any> | SorterResult<any>[]
  ) => {
    const restSorter = Array.isArray(sorter)
      ? sorter.length > 0
        ? sorter[0]
        : undefined
      : sorter;
    const searchCriteria = {
      pagination: newPagination,
      dto: searchDto,
      sorter: restSorter
    };
    await requestSearchBroadcastLists(searchCriteria);
    setPagination(newPagination);
    preferedRowsPerPageChanged(newPagination.pageSize);
  };

  useEffect(() => {
    void requestSearchBroadcastLists({
      pagination,
      sorter: {
        columnKey: "creationDate",
        order: orderDescend
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return requestSearchBroadcastLists.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setBroadcastLists(result.data.listBroadcastListDTO);
        setDataHasLoaded(true);
        setPagination({
          ...pagination,
          total: result.data.count
        });
        preferedRowsPerPageChanged(pagination.pageSize);
      } else {
        displayError("news.load_failed");
      }
    });
  });

  useEffect(() => {
    return requestDeleteBroadcastList.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteBroadcastList(params.id);
        message.success(t("broadcast_lists.global_buttons.delete.success"));
      } else {
        displayError("broadcast_lists.global_buttons.delete.error");
      }
    });
  });

  useEffect(() => {
    return requestDuplicateBroadcastList.done.watch(({ result }) => {
      if (result.ok && result.data) {
        navigate(`/broadcastLists/${result.data.id}`);
        message.success(t("status_survey.duplicate.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  return (
    <>
      <PageHeader>
        <PageTitle
          title={t(
            editMode ? "broadcast_lists.edit.title" : "broadcast_lists.title"
          )}
        />
      </PageHeader>
      <PageBody>
        <BroadcastListsListSearchForm
          onSearchDtoUpdate={setSearchDto}
          pagination={pagination}
          dataHasLoaded={dataHasLoaded}
          setDataHasLoaded={setDataHasLoaded}
        />
        <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
          <div className="flex-grow-1 d-flex align-items-center">
            <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
              <h3 className="text-secondary mb-0">
                {t("broadcast_lists.list_title")}
              </h3>
            </Divider>
          </div>
          <div className="d-flex flex-row align-items-center justify-content-end">
            {canCreate && (
              <Button
                type="default"
                className="btn-secondary m-2"
                onClick={() => {
                  navigate("/broadcastLists/add");
                }}
              >
                <PlusCircleOutlined /> {t("broadcast_lists.global_buttons.add")}
              </Button>
            )}
          </div>
        </div>
        <Table
          style={{ whiteSpace: "break-spaces" }}
          locale={{ emptyText: dataHasLoaded ? "" : " " }}
          loading={{ spinning: !dataHasLoaded, indicator: SpinIcon }}
          columns={columns(canDelete)}
          dataSource={broadcastListStore}
          pagination={pagination}
          onChange={handleTableChange}
          showSorterTooltip={false}
          rowKey={record => record.id}
          rowClassName="cursor-pointer"
          onRow={broadcastList => {
            return {
              onClick: () => navigate(`/broadcastLists/${broadcastList.id}`)
            };
          }}
        />
      </PageBody>
    </>
  );
};

export default BroadcastListsScreen;
